import type { OcrData, OcrExpenseField } from '../types';

/**
 * Builds a stable highlight ID for an expense field.
 * Must be used by all producers/consumers so format stays in sync.
 */
export function buildHighlightId(fieldType: string, index: number): string {
  return `${fieldType}-${index}`;
}

/** Textract field types that belong to line item rows */
const LINE_ITEM_FIELD_TYPES = new Set([
  'ITEM',
  'QUANTITY',
  'UNIT_PRICE',
  'PRICE',
  'EXPENSE_ROW_AMOUNT',
  'PRODUCT_CODE',
]);

/**
 * Enriches ocrData expense fields with bookingFieldMapping for line items
 * that were processed before backend mapping was added.
 * Groups consecutive line item fields into rows by fieldType reset pattern.
 */
export function enrichLineItemMappings(
  ocrData: OcrData | null | undefined
): OcrData | null | undefined {
  if (!ocrData?.expenseFields) return ocrData;

  // Check if any line item fields already have mapping — if so, backend handled it
  const hasLineItemMappings = ocrData.expenseFields.some(
    (f) => f.bookingFieldMapping?.startsWith('lineItem.')
  );
  if (hasLineItemMappings) return ocrData;

  // Find unmapped line item fields and assign row indices
  let rowIndex = -1;
  let lastWasLineItem = false;
  const seenTypesInRow = new Set<string>();

  const enrichedFields = ocrData.expenseFields.map((field) => {
    if (!LINE_ITEM_FIELD_TYPES.has(field.fieldType)) {
      lastWasLineItem = false;
      return field;
    }

    // Detect new row: first line item field, or a fieldType we've already seen in current row
    if (!lastWasLineItem || seenTypesInRow.has(field.fieldType)) {
      rowIndex++;
      seenTypesInRow.clear();
    }
    lastWasLineItem = true;
    seenTypesInRow.add(field.fieldType);

    // Only enrich if not already mapped
    if (field.bookingFieldMapping) return field;
    return { ...field, bookingFieldMapping: `lineItem.${rowIndex}` };
  });

  return { ...ocrData, expenseFields: enrichedFields };
}

/**
 * Groups expense fields by their bookingFieldMapping.
 * Example: 'voucherNumber' → [{ fieldType: 'INVOICE_RECEIPT_ID', valueBoundingBox: {...} }]
 */
export function buildFieldToExpenseMap(
  ocrData: OcrData | null | undefined
): Map<string, OcrExpenseField[]> {
  const map = new Map<string, OcrExpenseField[]>();
  if (!ocrData?.expenseFields) return map;

  for (const field of ocrData.expenseFields) {
    if (!field.bookingFieldMapping || !field.valueBoundingBox) continue;

    const existing = map.get(field.bookingFieldMapping) ?? [];
    existing.push(field);
    map.set(field.bookingFieldMapping, existing);
  }

  return map;
}

/**
 * Reverse map: expense field index ID → booking field name.
 * The ID format matches InvoicePreviewPanel: `${fieldType}-${index}`
 */
export function buildHighlightIdToFieldMap(
  ocrData: OcrData | null | undefined
): Map<string, string> {
  const map = new Map<string, string>();
  if (!ocrData?.expenseFields) return map;

  ocrData.expenseFields.forEach((field, index) => {
    if (!field.bookingFieldMapping || !field.valueBoundingBox) return;
    map.set(buildHighlightId(field.fieldType, index), field.bookingFieldMapping);
  });

  return map;
}
